import { useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { LayoutDashboard, LogIn, Menu, X } from 'lucide-react';

import { BrandLink, Signature } from '@/components/Brand';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Button, ButtonLink } from '@/components/ui/Button';
import { useAuth } from '@/features/auth/AuthContext';
import { cn } from '@/lib/cn';

const NAV = [
  { to: '/', label: 'Accueil' },
  { to: '/parcours', label: 'Parcours' },
  { to: '/projets', label: 'Projets' },
];

export function PublicLayout() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  return (
    <div className="flex min-h-screen flex-col">
      <a href="#contenu" className="skip-link">
        Aller au contenu principal
      </a>

      <header className="sticky top-0 z-40 border-b border-border bg-bg/85 backdrop-blur-lg">
        <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-4 sm:px-6 lg:px-8">
          <BrandLink />

          <nav aria-label="Navigation principale" className="ml-6 hidden items-center gap-1 md:flex">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  cn(
                    'rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    isActive ? 'bg-surface-2 text-fg' : 'text-muted hover:text-fg',
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <ThemeToggle />
            {user ? (
              <ButtonLink to="/app" size="sm" className="hidden sm:inline-flex">
                <LayoutDashboard className="h-4 w-4" />
                Mon espace
              </ButtonLink>
            ) : (
              <ButtonLink to="/connexion" size="sm" className="hidden sm:inline-flex">
                <LogIn className="h-4 w-4" />
                Se connecter
              </ButtonLink>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setOpen((value) => !value)}
              aria-expanded={open}
              aria-controls="menu-mobile"
              aria-label="Ouvrir le menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {open && (
          <nav
            id="menu-mobile"
            aria-label="Navigation mobile"
            className="border-t border-border bg-surface px-4 py-3 md:hidden"
          >
            <ul className="space-y-1">
              {NAV.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === '/'}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        'block rounded-lg px-3 py-2 text-sm font-medium',
                        isActive ? 'bg-surface-2 text-fg' : 'text-muted',
                      )
                    }
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
              <li className="pt-2">
                <ButtonLink
                  to={user ? '/app' : '/connexion'}
                  size="sm"
                  className="w-full"
                  onClick={() => setOpen(false)}
                >
                  {user ? 'Mon espace' : 'Se connecter'}
                </ButtonLink>
              </li>
            </ul>
          </nav>
        )}
      </header>

      <main id="contenu" className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-border bg-surface/40">
        <div className="mx-auto grid max-w-7xl gap-8 px-4 py-12 sm:px-6 md:grid-cols-4 lg:px-8">
          <div className="md:col-span-2">
            <BrandLink />
            <p className="mt-4 max-w-sm text-sm text-muted">
              Apprendre, construire et industrialiser l'intelligence artificielle. Formations
              professionnelles DevOps, DevSecOps, MLOps, LLMOps et ingénierie d'agents IA.
            </p>
            <Signature className="mt-4" />
          </div>
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-wide text-faint">Formations</h2>
            <ul className="mt-3 space-y-2 text-sm">
              <li>
                <Link to="/parcours/devops-ai-engineering" className="text-muted hover:text-fg">
                  DevOps &amp; AI Engineering
                </Link>
              </li>
              <li>
                <Link to="/parcours/mlops-ai-platform-engineering" className="text-muted hover:text-fg">
                  MLOps &amp; AI Platform
                </Link>
              </li>
              <li>
                <Link to="/parcours/ai-agents-engineering" className="text-muted hover:text-fg">
                  AI Agents Engineering
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-wide text-faint">Plateforme</h2>
            <ul className="mt-3 space-y-2 text-sm">
              <li>
                <Link to="/parcours" className="text-muted hover:text-fg">
                  Catalogue des modules
                </Link>
              </li>
              <li>
                <Link to="/projets" className="text-muted hover:text-fg">
                  Projets professionnels
                </Link>
              </li>
              <li>
                <Link to="/connexion" className="text-muted hover:text-fg">
                  Espace apprenant
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-border px-4 py-5 text-center text-xs text-faint sm:px-6 lg:px-8">
          © {new Date().getFullYear()} KH AI Academy — Tous droits réservés.
        </div>
      </footer>
    </div>
  );
}
